import type { BenchmarkDeviceView, BenchmarkTotals } from '../account/api';
import { getStoredSession } from '../account/session';
import { pullBenchmarks } from '../account/sync';
import type { BenchmarkStats } from '../db/benchmarks-repo';

function savedText(before: number, after: number): string {
  const saved = Math.max(0, before - after);
  const pct = before > 0 ? (saved / before) * 100 : 0;
  return before > 0 ? `${saved.toLocaleString()} tok (${pct.toFixed(0)}%)` : '—';
}

export class BenchmarksPanel {
  private root: HTMLElement;
  private thisDevice: BenchmarkStats;
  private serverDevices: BenchmarkDeviceView[] | null = null;
  private serverTotals: BenchmarkTotals | null = null;
  private refreshing = false;

  constructor(container: HTMLElement, initialStats: BenchmarkStats) {
    this.root = container;
    this.thisDevice = initialStats;
    this.render();
    if (getStoredSession()) void this.refreshFromServer();
  }

  updateThisDevice(stats: BenchmarkStats) {
    this.thisDevice = stats;
    this.render();
  }

  private async refreshFromServer() {
    this.refreshing = true;
    this.render();
    try {
      const { devices, totals } = await pullBenchmarks();
      this.serverDevices = devices;
      this.serverTotals = totals;
    } catch {
      // Best-effort — this device's own lifetime stats still render regardless.
    } finally {
      this.refreshing = false;
      this.render();
    }
  }

  private render() {
    this.root.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'pane-header';
    header.innerHTML = '<span class="pane-title">Benchmarks</span>';
    if (getStoredSession()) {
      const refreshBtn = document.createElement('button');
      refreshBtn.className = 'btn subtle';
      refreshBtn.textContent = this.refreshing ? 'Refreshing…' : 'Refresh';
      refreshBtn.disabled = this.refreshing;
      refreshBtn.addEventListener('click', () => void this.refreshFromServer());
      header.appendChild(refreshBtn);
    }
    this.root.appendChild(header);

    const body = document.createElement('div');
    body.className = 'benchmarks-body';

    const deviceBlock = document.createElement('div');
    deviceBlock.innerHTML = `
      <div class="benchmarks-section-label">This device — lifetime</div>
      <div id="benchmarks-this-device" class="grid">
        <div class="stat"><div class="label">Requests</div><div class="value mono">${this.thisDevice.requests.toLocaleString()}</div></div>
        <div class="stat"><div class="label">Est. cost</div><div class="value mono">$${this.thisDevice.costUsd.toFixed(4)}</div></div>
        <div class="stat savings"><div class="label">Tokens saved</div><div class="value mono">${savedText(this.thisDevice.tokensBefore, this.thisDevice.tokensAfter)}</div></div>
        <div class="stat"><div class="label">Cache read</div><div class="value mono">${this.thisDevice.cacheReadTokens.toLocaleString()}</div></div>
      </div>`;
    body.appendChild(deviceBlock);

    if (!getStoredSession()) {
      const hint = document.createElement('div');
      hint.className = 'benchmarks-hint';
      hint.textContent = 'Sign in from the Account button to total these across all your devices.';
      body.appendChild(hint);
    } else if (this.serverTotals && this.serverDevices) {
      const totals = this.serverTotals;
      const allBlock = document.createElement('div');
      allBlock.innerHTML = `
        <div class="benchmarks-section-label">Across all your devices</div>
        <div class="grid">
          <div class="stat"><div class="label">Requests</div><div class="value mono">${totals.requests.toLocaleString()}</div></div>
          <div class="stat"><div class="label">Est. cost</div><div class="value mono">$${totals.costUsd.toFixed(4)}</div></div>
          <div class="stat savings"><div class="label">Tokens saved</div><div class="value mono">${savedText(totals.tokensBefore, totals.tokensAfter)}</div></div>
          <div class="stat"><div class="label">Devices</div><div class="value mono">${this.serverDevices.length}</div></div>
        </div>`;
      body.appendChild(allBlock);

      if (this.serverDevices.length > 0) {
        const list = document.createElement('div');
        list.className = 'benchmarks-device-list';
        for (const d of this.serverDevices) {
          const row = document.createElement('div');
          row.className = 'benchmarks-device-row';
          const label = d.label || `Device ${d.deviceId.slice(0, 8)}`;
          row.innerHTML = `<span class="benchmarks-device-label" title="${d.label ?? d.deviceId}">${label}</span><span>${d.requests.toLocaleString()} req</span><span>${savedText(d.tokensBefore, d.tokensAfter)}</span>`;
          list.appendChild(row);
        }
        body.appendChild(list);
      }
    }

    this.root.appendChild(body);
  }
}
